import { IconName, PalmettoIcon } from '@palmetto/dsm-components';
import { useTranslation, FlowStepProps, GenericStepLayout, GenericStepLayoutProps } from '@helios/frontend-core';

import { openHome } from '../../../IntercomBubble/IntercomBubble';
import { ConnectionScreens } from './UtilityConnectionCompleted';

const animationIcons: IconName[] = [
  'bolt',
  'moneyDollarSign',
  'calendarDaily',
  'plugOutletPowerElectricity',
  'homeElectricityBolt',
  'partlyCloudy',
  'powerPlantGridUtility',
];

const translationPrefix = 'connectUtilityTakeover.steps.utility-connect-completed';

export interface Props {
  displayScreen: ConnectionScreens;
}

export function UtilityConnectionCompletedView({ displayScreen, nextStep, previousStep }: Props & FlowStepProps) {
  const { t } = useTranslation();

  const getTranslation = (status: string, value: string) => {
    return t(`${translationPrefix}.${status}.${value}`);
  };

  const connectionStatusMap: Record<ConnectionScreens, Omit<GenericStepLayoutProps, 'trackingId'>> = {
    [ConnectionScreens.IN_PROGRESS]: {
      title: getTranslation('in-progress', 'title'),
      description: getTranslation('in-progress', 'description'),
      icon: animationIcons,
    },
    [ConnectionScreens.CONNECTED]: {
      title: getTranslation('connected', 'title'),
      description: getTranslation('connected', 'description'),
      icon: 'check',
      primaryCTA: {
        label: t('common.finish'),
        onClick: () => {
          nextStep();
        },
      },
    },
    [ConnectionScreens.CHECK_AGAIN_LATER]: {
      title: getTranslation('check-again-later', 'title'),
      description: getTranslation('check-again-later', 'description'),
      icon: 'palmTree',
      primaryCTA: {
        label: t('common.continue'),
        onClick: () => {
          nextStep();
        },
      },
    },
    [ConnectionScreens.EMAIL_APPROVAL_NEEDED]: {
      title: getTranslation('email-approval-needed', 'title'),
      description: getTranslation('email-approval-needed', 'description'),
      icon: 'mail',
      primaryCTA: {
        label: t('common.finish'),
        onClick: () => {
          nextStep();
        },
      },
    },
    [ConnectionScreens.RETRY_2FA]: {
      title: getTranslation('retry-2fa', 'title'),
      description: getTranslation('retry-2fa', 'description'),
      icon: 'lock',
      primaryCTA: {
        leadingIcon: <PalmettoIcon name="refresh" />,
        label: t('connectUtilityTakeover.retry-connection'),
        onClick: () => {
          previousStep();
        },
      },
    },
    [ConnectionScreens.USERNAME_OR_PASSWORD_INCORRECT]: {
      title: getTranslation('incorrect-credentials', 'title'),
      description: getTranslation('incorrect-credentials', 'description'),
      icon: 'dangerous',
      primaryCTA: {
        label: t('connectUtilityTakeover.retry-connection'),
        leadingIcon: <PalmettoIcon name="refresh" />,
        onClick: () => {
          previousStep();
        },
      },
    },
    [ConnectionScreens.CONNECTION_FAILED]: {
      title: getTranslation('connection-failed', 'title'),
      description: getTranslation('connection-failed', 'description'),
      icon: 'dangerous',
      primaryCTA: {
        label: t('connectUtilityTakeover.retry-connection'),
        leadingIcon: <PalmettoIcon name="refresh" />,
        onClick: () => {
          previousStep();
        },
      },
      secondaryCTA: {
        label: t('common.contactSupport'),
        leadingIcon: <PalmettoIcon name="chat" />,
        onClick: () => {
          nextStep();
          openHome();
        },
      },
    },
  };

  return (
    <GenericStepLayout
      icon={connectionStatusMap[displayScreen].icon}
      title={connectionStatusMap[displayScreen].title}
      description={connectionStatusMap[displayScreen].description}
      primaryCTA={connectionStatusMap[displayScreen].primaryCTA}
      secondaryCTA={connectionStatusMap[displayScreen].secondaryCTA}
      trackingId={`takeover-utility-connection-${displayScreen}-step`}
    />
  );
}

export default UtilityConnectionCompletedView;
